// Columns that offer a fixed list of values, drawn as chips.
//
// Google Sheets calls it a dropdown; the useful half is not the input widget but
// the CHIP — a column of statuses reads as a column of statuses at a glance,
// where the same words in plain text read as a wall.
//
// The list is a suggestion, not a rule. A value that is not on it still goes in
// and is marked, because a table over live project data that refuses what the
// project actually contains is worse than one that flags it.

import { FIRST_FILTERABLE_ROW } from './filters';
import type { ColumnList, FieldBinding, Sheet } from './types';

/** The chip palette, cycled. Hue-distinct, like the reference colours. */
export const CHIP_COLOURS = [
  '#3370bd',
  '#6fa86a',
  '#e6b840',
  '#a86fc2',
  '#e8884a',
  '#0f9d8a',
  '#e8562d',
  '#545e6a',
] as const;

export function chipColour(index: number): string {
  return CHIP_COLOURS[index % CHIP_COLOURS.length]!;
}

export function listAt(sheet: Sheet, col: number): ColumnList | undefined {
  return usable(sheet.dropdowns?.[col]);
}

export function rowListAt(sheet: Sheet, row: number): ColumnList | undefined {
  return usable(sheet.rowDropdowns?.[row]);
}

/**
 * The list a single cell obeys.
 *
 * Column first, then row — see `Sheet.rowDropdowns` for why that way round.
 * A list BOUND to a field is usable even with no options saved yet, because
 * the options come from the project rather than from the document.
 *
 * Row 1 is exempt from a COLUMN list: it holds the header the column labels
 * itself with, and a chip there turns the name of the column into one of its
 * values. A row binding has no equivalent — the person named that row, so it
 * applies across the whole of it.
 */
export function listForCell(sheet: Sheet, row: number, col: number): ColumnList | undefined {
  const column = row >= FIRST_FILTERABLE_ROW ? listAt(sheet, col) : undefined;
  return column ?? rowListAt(sheet, row);
}

function usable(list: ColumnList | undefined): ColumnList | undefined {
  if (!list) return undefined;
  return list.options.length > 0 || list.field ? list : undefined;
}

/**
 * A bound list with its options filled in from the project.
 *
 * The caller supplies the values because this file must not reach for the
 * SDK — the model stays testable with plain fixtures, and the one place that
 * has the data does the asking.
 *
 * An empty answer keeps the saved options: a project that has not loaded yet
 * reports no values at all, and emptying the column on the way past would
 * look exactly like the list having been deleted.
 */
export function resolveList(list: ColumnList, values: readonly string[]): ColumnList {
  if (!list.field) return list;
  const options = suggestOptions(values);
  if (options.length === 0) return list;
  return { ...list, options };
}

const SOURCE_LABELS: Record<FieldBinding['source'], string> = {
  areas: 'areas',
  bakedSections: 'baked sections',
  rawSections: 'raw sections',
  usages: 'usages',
};

/** What the menu calls a binding: `usage on baked sections`. */
export function bindingLabel(field: FieldBinding): string {
  return `${field.name} on ${SOURCE_LABELS[field.source]}`;
}

/**
 * Where a value sits in the list, matched without case.
 *
 * `-1` for a value the list does not have, which is what marks a chip as
 * off-list rather than hiding it.
 */
export function optionIndex(list: ColumnList, value: string): number {
  const t = value.trim().toLowerCase();
  return list.options.findIndex((o) => o.trim().toLowerCase() === t);
}

export function colourFor(list: ColumnList, index: number): string {
  if (index < 0) return '#545e6a';
  return list.colours?.[index] ?? chipColour(index);
}

/** The options someone typed into the editor, one per line, tidied. */
export function parseOptions(text: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const line of text.split(/[\n,]/)) {
    const t = line.trim();
    if (t === '') continue;
    const key = t.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(t);
  }
  return out;
}

/**
 * The distinct values a column already holds — the obvious first draft of a
 * list, and the reason making one takes no typing on a column of real data.
 */
export function suggestOptions(values: readonly string[]): string[] {
  return parseOptions(values.join('\n')).slice(0, 40);
}
